// Simple fetch-based Sanity service to avoid ES module compatibility issues
const SANITY_PROJECT_ID = "yjk6ecgf";
const SANITY_DATASET = "production";
const SANITY_API_VERSION = "2023-01-01";

// Service to fetch services landing page data using direct API calls
const SanityService = {
  // Fetch specific service landing page by slug
  async getServiceLandingPageBySlug(slug) {
    try {
      const query = `*[_type == "serviceslandingPage" && slug.current == "${slug}" && isActive == true][0] {
        title,
        slug,
        pageType,
        seoMeta {
          seoTitle,
          seoDescription,
          seoKeywords
        },
        heroSection {
          title,
          subtitle,
          description,
          mediaType,
          heroImage {
            asset -> {
              _id,
              url
            }
          },
          heroVideo {
            videoUrl,
            videoFile {
              asset -> {
                url
              }
            },
            posterImage {
              asset -> {
                url
              }
            }
          },
          heroForm {
            formTitle,
            formSubtitle,
            submitButtonText,
            successMessage
          },
          primaryCta {
            enabled,
            text,
            link,
            openInNewTab
          },
          secondaryCta {
            enabled,
            text,
            link,
            openInNewTab
          }
        },
        approachSection {
          enabled,
          title,
          approaches[] {
            label,
            details,
            image {
              asset -> {
                _id,
                url
              }
            }
          }
        },
        deliverablesSection {
          enabled,
          title,
          subtitle,
          deliverables
        },
        servicesSection {
          enabled,
          title,
          ctaText,
          ctaUrl,
          services[] {
            label,
            details,
            icon {
              asset -> {
                _id,
                url
              }
            },
            serviceUrl
          }
        },
        benefitsSection {
          enabled,
          title,
          subtitle,
          benefits[] {
            title,
            description,
            icon {
              asset -> {
                _id,
                url
              }
            }
          }
        },
        howItWorksSection {
          enabled,
          title,
          subtitle,
          steps[] {
            stepNumber,
            title,
            description,
            icon {
              asset -> {
                _id,
                url
              }
            }
          }
        },
        caseStudiesSection {
          enabled,
          type,
          title,
          subtitle,
          description,
          ctaText,
          ctaUrl
        },
        faqSection {
          enabled,
          title,
          subtitle,
          faqs[] {
            question,
            answer
          }
        },
        finalCtaSection {
          enabled,
          title,
          subtitle,
          buttonText,
          buttonLink,
          backgroundColor
        },
        publishedAt,
        isActive
      }`;

      const url = `https://${SANITY_PROJECT_ID}.api.sanity.io/v${SANITY_API_VERSION}/data/query/${SANITY_DATASET}?query=${encodeURIComponent(
        query
      )}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.result;
    } catch (error) {
      console.error("Error fetching service landing page:", error);
      return null;
    }
  },

  // Fetch offerings/cards by page type for Consult/Audit/Build pages
  async getOfferingsByPageType(pageType) {
    try {
      const query = `*[_type == "serviceslandingPage" && pageType == "${pageType}" && isActive == true] | order(orderRank) {
        _id,
        title,
        pageType,
        slug,
        orderRank,
        cardSummary {
          label,
          description,
          icon {
            asset -> {
              _id,
              url
            }
          },
          cta1 {
            title,
            url,
            openInNewTab
          },
          cta2 {
            title,
            url,
            openInNewTab
          }
        }
      }`;

      const url = `https://${SANITY_PROJECT_ID}.api.sanity.io/v${SANITY_API_VERSION}/data/query/${SANITY_DATASET}?query=${encodeURIComponent(
        query
      )}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform the data to match the expected format for OurOffering component
      return data.result.map((node) => {
        // Build CTA1 (Primary button - default: Ask An Expert -> /contact-us)
        const cta1Title =
          node.cardSummary.cta1?.title ||
          `Ask About Our ${node.cardSummary.label}`;
        const cta1Url = node.cardSummary.cta1?.url || "/contact-us";
        const cta1NewTab = node.cardSummary.cta1?.openInNewTab || false;

        // Build CTA2 (Secondary button - default: View Details -> page URL)
        const cta2Title = node.cardSummary.cta2?.title || "View Details";
        const cta2Url =
          node.cardSummary.cta2?.url || `/services/${node.slug.current}`;
        const cta2NewTab = node.cardSummary.cta2?.openInNewTab || false;

        return {
          label: node.cardSummary.label,
          details: node.cardSummary.description,
          buttonName: cta1Title,
          buttonUrl: cta1Url,
          buttonNewTab: cta1NewTab,
          secondaryButtonName: cta2Title,
          secondaryButtonUrl: cta2Url,
          secondaryButtonNewTab: cta2NewTab,
          image: node.cardSummary.icon?.asset?.url || null,
          path: `/services/${node.slug.current}`,
          // Additional data for reference
          _id: node._id,
          pageTitle: node.title,
        };
      });
    } catch (error) {
      console.error("Error fetching offerings by page type:", error);
      return [];
    }
  },
};

module.exports = { SanityService };
module.exports.default = SanityService;
